import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

const Header = ({ sidebarCollapsed, onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut, profileLoading } = useAuth();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      const { error } = await signOut();
      if (!error) {
        navigate('/login-authentication');
      } else {
        console.error('Sign out error:', error);
      }
    } catch (err) {
      console.error('Sign out failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = () => {
    if (profileLoading) return 'Loading...';
    return userProfile?.full_name || userProfile?.fullName || user?.email || 'User';
  };

  const getUserRole = () => {
    if (profileLoading) return '';
    return userProfile?.role ? userProfile?.role?.replace('_', ' ')?.toUpperCase() : '';
  };

  return (
    <header className={`
      fixed top-0 right-0 z-40 h-16 bg-card border-b border-border
      transition-smooth flex items-center justify-between px-4 md:px-6
      ${sidebarCollapsed ? 'md:left-16' : 'md:left-240'} left-0
    `}>
      {/* Left Section - Mobile Menu + Search */}
      <div className="flex items-center space-x-2 md:space-x-4 flex-1">
        {/* Mobile Menu Button */}
        <button
          onClick={onMobileMenuToggle}
          className="md:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <Icon name="Menu" size={20} />
        </button>

        {/* Search - Hidden on mobile */}
        <div className="relative max-w-md w-full hidden md:block">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
          />
          <input
            type="text"
            placeholder="Search employees, attendance..."
            className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-lg 
                     focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                     text-sm placeholder:text-muted-foreground"
          />
        </div>
      </div>
      {/* Right Section - User Profile */}
      <div className="flex items-center space-x-2 md:space-x-4">
        {/* Notifications - Hidden text on mobile */}
        <button className="relative p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors">
          <Icon name="Bell" size={20} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full"></span>
        </button>

        {/* User Profile Dropdown */}
        <div className="relative">
          <button
            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            className="flex items-center space-x-2 md:space-x-3 p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              {userProfile?.avatarUrl ? (
                <img 
                  src={userProfile?.avatarUrl} 
                  alt={getUserDisplayName()}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-sm font-semibold">
                  {getUserDisplayName()?.charAt(0)?.toUpperCase()}
                </span>
              )}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-foreground">
                {getUserDisplayName()}
              </p>
              <p className="text-xs text-muted-foreground">
                {getUserRole()}
              </p>
            </div>
            <Icon 
              name="ChevronDown" 
              size={16} 
              className={`hidden md:block text-muted-foreground transition-transform ${
                profileDropdownOpen ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Dropdown Menu - Responsive positioning */}
          {profileDropdownOpen && (
            <>
              {/* Backdrop for mobile */}
              <div 
                className="fixed inset-0 z-40 md:hidden" 
                onClick={() => setProfileDropdownOpen(false)}
              />
              <div className="absolute right-0 top-12 w-56 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                <div className="px-4 py-2 border-b border-border">
                  <p className="text-sm font-medium text-foreground">
                    {getUserDisplayName()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {userProfile?.username || user?.email}
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    navigate('/employee-dashboard');
                    setProfileDropdownOpen(false);
                  }}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                >
                  <Icon name="Home" size={16} />
                  <span>Dashboard</span>
                </button>
                
                <div className="border-t border-border my-2"></div>
                
                <button
                  onClick={handleSignOut}
                  disabled={loading}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Icon name="LogOut" size={16} />
                  )}
                  <span>Sign Out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;